import type { Toast } from './state'
import { toasts } from './state'

type ToastMeta = {
  timeoutId: ReturnType<typeof setTimeout>
  startTime: number
  remaining: number
  duration: number
  paused: boolean
}

const timers = new Map<string, ToastMeta>()

export const useToast = () => {
  const show = (toast: Omit<Toast, 'id'>) => {
    const id = crypto.randomUUID()
    toasts.value.push({
      id,
      ...toast,
    })

    const timeoutId = setTimeout(() => closeToast(id), 5000)

    timers.set(id, {
      timeoutId,
      startTime: Date.now(),
      remaining: 5000,
      duration: 5000,
      paused: false,
    })
  }

  const pauseToast = (id: string) => {
    const meta = timers.get(id)

    if (!meta) return

    const elapsed = Date.now() - meta.startTime

    meta.remaining -= elapsed
    meta.paused = true

    clearTimeout(meta.timeoutId)
  }
  const resumeToast = (id: string) => {
    const meta = timers.get(id)

    if (!meta) return

    meta.timeoutId = setTimeout(() => closeToast(id), meta.remaining)

    meta.paused = false
    meta.startTime = Date.now()

    timers.set(id, meta)
  }

  const progressToast = (id: string) => {
    const meta = timers.get(id)

    if (!meta) return 0

    if (meta.paused) {
      return (meta.remaining / meta.duration) * 100
    }

    const elapsed = Date.now() - meta.startTime

    const currentRemaining = meta.remaining - elapsed

    let progress = (currentRemaining / meta.duration) * 100

    if (progress < 0) {
      progress = 0
    }

    return progress
  }

  const error = (message: string) => {
    show({
      variant: 'error',
      title: 'Ошибка',
      message,
    })
  }
  const success = (message: string) => {
    show({
      variant: 'success',
      title: 'Успешно',
      message,
    })
  }
  const warning = (message: string) => {
    show({
      variant: 'warning',
      title: 'Предупреждение',
      message,
    })
  }
  const info = (message: string) => {
    show({
      variant: 'info',
      title: 'Информация',
      message,
    })
  }

  const closeToast = (id: string) => {
    const index = toasts.value.findIndex((toast) => toast.id === id)

    if (index !== -1) {
      toasts.value.splice(index, 1)
    }

    timers.delete(id)
  }

  return {
    show,
    error,
    success,
    warning,
    info,
    closeToast,
    pauseToast,
    resumeToast,
    progressToast,
  }
}
